import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpApiOptions } from './http-api-options';
import { KeyValue } from '@angular/common';
import { first } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
/**
 * Service for managing HTTP requests to API
 */
export class HttpApiService {
  /**
   * HTTP code for SUCCESS
   */
  private static HTTP_SUCCESS = 200;
  /**
   * HTTP code for CREATED
   */
   private static HTTP_CREATED = 201;
   /**
   * HTTP code for ACCEPTED
   */
    private static HTTP_ACCEPTED = 202;
   /**
   * HTTP code for invalid input
   */
  private static HTTP_INVALID_INPUT = 400;
  /**
   * HTTP code for unathorized
   */
  private static HTTP_UNAUTHORIZED = 401;
  /**
   * HTTP code for forbidden
   */
  private static HTTP_FORBIDDEN = 403;
  /**
   * HTTP code for Not Found
   */
  private static HTTP_NOT_FOUND = 404;
  /**
   * HTTP code for Not Accepted
   */
   private static HTTP_NOT_ACCEPTED = 406;
  /**
   * HTTP code for Unprocessable
   */
  private static HTTP_UNPROCESSABLE = 422;
    /**
   * HTTP code for Multiple Choices
   */
  private static HTTP_MULTIPLE_CHOICES = 300;

  /**
   * User token
   */
  private token_: string = '';

  /**
   * URL to redirect when forbidden
   */
private _forbiddenRedirectUrl: string = '';


  /**
   * Get forbidden redirect URL
   * @returns forbidden redirect URL
   */
  public get forbiddenRedirectUrl(): string {
    return this._forbiddenRedirectUrl;
  }

  /**
   * Set forbidden redirect URL
   * @param url forbidden redirect URL
   */
  public set forbiddenRedirectUrl(url: string) {
    this._forbiddenRedirectUrl = url;
  }


  get token(): string {
    return this.token_;
  }

  set token(token: string){
    this.token_ = token;
  }

  constructor(private http: HttpClient, private router:Router) {}

  /**
   * Do a POST API HTTP request
   * @param postData data to be posted
   * @param apiUrl URL of the API endpoint
   * @param options HTTP options for this request
   * @returns a promise for the response
   */
  public postApi(
    apiUrl: string,
    postData?: object,
    options?: HttpApiOptions
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestHttpOptions(options).then(
        (httpOptions) => {
          const body = JSON.stringify(postData);
          this.http
            .post(apiUrl, body, httpOptions)
            .pipe(first())
            .subscribe({
              next: (data) => {
                console.log('HttpApi POST RESOLVED');
                resolve(data);
              },
              error: (errorPost) => {
                console.log('HttpApi POST ERROR');
                console.log(errorPost);

              //If forbidden redirect to forbidden redirect URL
                if(this.wasUnauthorized(errorPost.status)){
                 this.router.navigate([this.forbiddenRedirectUrl]);
                }

                reject(errorPost);
              },
              complete: () => console.info('HttpApi POST COMPLETED'),
            });
        },
        (err) => {


          reject(err);
        }
      );
    });
  }

  /**
   * Do a PUT API HTTP request
   * @param putData data to be put
   * @param apiUrl URL of the API endpoint
   * @param options HTTP options for this request
   * @returns a promise for the response
   */
  public putApi(
    putData: object,
    apiUrl: string,
    options?: HttpApiOptions
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestHttpOptions(options).then(
        (httpOptions) => {

          const body = JSON.stringify(putData);

          console.log("BODY: " + body);

          this.http
            .put(apiUrl, body, httpOptions)
            .pipe(first())
            .subscribe({
              next: (data) => {
                console.log('HttpApi PUT RESOLVED');
                resolve(data);
              },
              error: (errorPut) => {
                console.log('HttpApi PUT ERROR');
                console.log(errorPut);

                 //If forbidden redirect to forbidden redirect URL
                 if(this.wasUnauthorized(errorPut.status)){
                  this.router.navigate([this.forbiddenRedirectUrl]);
                 }
                reject(errorPut);
              },
              complete: () => console.info('HttpApi PUT COMPLETED'),
            });
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Do a GET API HTTP request
   * @param getData collection of params for GET request
   * @param apiUrl URL of the API endpoint
   * @param options HTTP options for this request
   * @returns a promise for the response
   */
  public getApi(
    apiUrl: string,
    options?: HttpApiOptions,
    getData?: KeyValue<string, string>[]
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestHttpOptions(options).then(
        (httpOptions) => {
          if (getData) {
            let params = new HttpParams();
            getData.forEach((kv) => (params = params.append(kv.key, kv.value)));
            httpOptions.params = params;
          }
          if(options?.withCredentials){
            httpOptions.withCredentials = true;
          }
          console.log(httpOptions);

          this.http
            .get(apiUrl, httpOptions)
            .pipe(first())
            .subscribe({
              next: (data) => {
                console.log('HttpApi GET RESOLVED');
                resolve(data);
              },
              error: (errorGet) => {
                console.log('HttpApi GET ERROR');
                console.log(errorGet);
                 //If forbidden redirect to forbidden redirect URL
                 if(this.wasUnauthorized(errorGet.status)){
                  this.router.navigate([this.forbiddenRedirectUrl]);
                 }
                reject(errorGet);
              },
              complete: () => console.info('HttpApi GET COMPLETED'),
            });
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Do a PATCH API HTTP request
   * @param patchData data to be patched
   * @param apiUrl URL of the API endpoint
   * @param options HTTP options for this request
   * @returns a promise for the response
   */
  public patchApi(
    patchData: object,
    apiUrl: string,
    options?: HttpApiOptions
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestHttpOptions(options).then(
        (httpOptions) => {
          const body = JSON.stringify(patchData);

          this.http
            .patch(apiUrl, body, httpOptions)
            .pipe(first())
            .subscribe({
              next: (data) => {
                console.log('HttpApi PATCH RESOLVED');
                resolve(data);
              },
              error: (errorPatch) => {
                console.log('HttpApi PATCH ERROR');
                console.log(errorPatch);
                 //If forbidden redirect to forbidden redirect URL
                 if(this.wasUnauthorized(errorPatch.status)){
                  this.router.navigate([this.forbiddenRedirectUrl]);
                 }

                reject(errorPatch);
              },
              complete: () => console.info('HttpApi PATCH COMPLETED'),
            });
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Do a DELETE API HTTP request
   * @param deleteData data to be deleted
   * @param apiUrl URL of the API endpoint
   * @param options HTTP options for this request
   * @returns a promise for the response
   */
  public deleteApi(
    deleteData: object,
    apiUrl: string,
    options?: HttpApiOptions
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestHttpOptions(options).then(
        (httpOptions) => {
          const body = JSON.stringify(deleteData);
          httpOptions.body = body;

          this.http
            .delete(apiUrl, httpOptions)
            .pipe(first())
            .subscribe({
              next: (data) => {
                console.log('HttpApi DELETE RESOLVED');
                resolve(data);
              },
              error: (errorDelete) => {
                console.log('HttpApi DELETE ERROR');
                console.log(errorDelete);
                 //If forbidden redirect to forbidden redirect URL
                 if(this.wasUnauthorized(errorDelete.status)){
                  this.router.navigate([this.forbiddenRedirectUrl]);
                 }

                reject(errorDelete);
              },
              complete: () => console.info('HttpApi DELETE COMPLETED'),
            });
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Build HTTP header adding Auth Token if needed
   * @param options options for building HTTP Header
   * @returns a promise for returning http header built
   */
  private requestHttpOptions(
    options: HttpApiOptions | undefined
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const token = options?.authNonRequired ? '' : this.token_;
      resolve(this.buildHttpOptions(token));
    });
  }

  /**
   * Get HTTP header
   * @param token auth token
   * @returns the HTTP header
   */
   private buildHttpOptions(token: string): any {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json;charset=utf-8',
        Authorization: ' Bearer ' + token,
      }),
      params: {},
    };
  }

  /**
   * Check if returned error was not found
   * @param status the returned status code
   * @returns true if was not found error
   */
  public wasNotFoundError(status: number): boolean {
    return status === HttpApiService.HTTP_NOT_FOUND;
  }

  /**
   * Check if returned error was not accepted
   * @param status the returned status code
   * @returns true if was not accepted error
   */
   public wasNotAcceptedError(status: number): boolean {
    return status === HttpApiService.HTTP_NOT_ACCEPTED;
  }

  /**
   * Check if returned error was Not found
   * @param status the returned status code
   * @returns true if was not found error
   */
  public wasUnprocessable(status: number): boolean {
    return status === HttpApiService.HTTP_UNPROCESSABLE;
  }

/**
   * Check if returned error was Invalid Input
   * @param status the returned status code
   * @returns true if was invalid input
   */
 public wasInvalidInput(status: number): boolean {
  return status === HttpApiService.HTTP_INVALID_INPUT;
}


  /**
   * Check if returned error was No authorized
   * @param status the returned status code
   * @returns true if was No authorized error
   */
  public wasUnauthorized(status: number): boolean {
    return status === HttpApiService.HTTP_UNAUTHORIZED;
  }

  /**
   * Check if returned error was Not found
   * @param status the returned status code
   * @returns true if was not found error
   */
  public wasForbidden(status: number): boolean {
    return status === HttpApiService.HTTP_FORBIDDEN;
  }

  /**
   * Check if not error returned
   * @param status the returned status code
   * @returns true if was not error
   */
     public wasSuccess(status: number): boolean {
      return status === HttpApiService.HTTP_SUCCESS;
    }


  /**
   * Check if object was created
   * @param status the returned status code
   * @returns true if was created
   */
     public wasCreated(status: number): boolean {
      return status === HttpApiService.HTTP_CREATED;
    }

  /**
   * Check if object was accepted
   * @param status the returned status code
   * @returns true if was created
   */
     public wasAccepted(status: number): boolean {
      return status === HttpApiService.HTTP_ACCEPTED;
    }

   /**
   * Check if multiple choices response.  This happens when one item is expected but multiple items were got
   * @param status the returned status code
   * @returns true if was multiple choice
   */
    public wasMultipleChoice(status: number): boolean {
      return status === HttpApiService.HTTP_MULTIPLE_CHOICES;
    }
}
